import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  ExternalLink,
  FileText,
  RefreshCw,
  Send,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  Button,
  Chip,
  Input,
  Modal,
  PartnerPicker,
  RichTextEditor,
  Select,
} from '@/components/ui'
import { EmailThreadPanel, SignaturePicker } from '@/shell/services/email-thread'
import { jsonFetch } from '@/lib/api/jsonFetch'
import { applySignatureToBody } from '@/lib/utils/signature'
import { odooUrl } from '@/lib/utils/odoo-url'
import { sanitizeEmailHtml } from '@/lib/utils/sanitize-html'
import { useHasAnyPermission } from '@/shell/module-system/permissions'
import {
  useAcknowledgeIntake,
  useCheckDuplicate,
  useConfirmIntake,
  useDismissWarnings,
  useIntakeDetail,
  useLinkOdooSo,
  useRejectIntake,
  useRetryExtract,
  useSendIntakeResponse,
  useTransitionIntake,
} from '../api/queries'
import type { DuplicateResponse, IntakeDetail, Stage } from '../api/types'
import {
  STAGE_LABELS,
  STAGE_VARIANT,
  allowedTargetsFrom,
  isTerminal,
} from '../utils/stage-meta'
import { DraftSummaryCard } from './DraftSummaryCard'
import { SalesIntakeChatPanel } from './SalesIntakeChatPanel'
import { SalesIntakeLineGrid } from './SalesIntakeLineGrid'
import { StageHistoryTimeline } from './StageHistoryTimeline'

export interface SalesIntakeWorkViewProps {
  intakeId: string
}

/**
 * Full-page per-intake working view (email-thread-viewer sprint).
 *
 * Replaces the slide-out SalesIntakeDetailDrawer for the "work an order"
 * role. Header band with a back-to-board link, then a 2-column grid: LEFT
 * the rich working content (header edits, draft summary, line grid,
 * pipeline transitions, history); RIGHT a stacked column with the source
 * email preview on top and CARLA's per-intake chat below. The two columns
 * scroll independently per the repo's scroll contract.
 */
export function SalesIntakeWorkView({ intakeId }: SalesIntakeWorkViewProps) {
  const navigate = useNavigate()
  const detail = useIntakeDetail(intakeId)
  const data = detail.data

  const [duplicate, setDuplicate] = useState<DuplicateResponse | null>(null)
  const checkDup = useCheckDuplicate()
  const linkOdooSo = useLinkOdooSo()
  const confirm = useConfirmIntake()
  const reject = useRejectIntake()
  const retry = useRetryExtract()
  const transition = useTransitionIntake()
  const acknowledge = useAcknowledgeIntake()
  const dismissWarnings = useDismissWarnings()
  const qc = useQueryClient()

  // Auto-fire duplicate check when detail loads (or refreshes) for
  // intakes in any non-terminal stage where confirm is still possible.
  // Skip if already linked to an Odoo SO — duplicate is resolved.
  useEffect(() => {
    if (
      data &&
      !isTerminal(data.status) &&
      data.customer_id != null &&
      data.customer_po_number &&
      !data.odoo_so_id
    ) {
      checkDup.mutateAsync(data.id).then(setDuplicate).catch(() => setDuplicate(null))
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- clears stale duplicate when intake leaves the dup-eligible state.
      setDuplicate(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, data?.status, data?.customer_id, data?.customer_po_number])

  const headerEdit = useMutation({
    mutationFn: (vars: {
      customer_id?: number
      customer_name?: string
      customer_po_number?: string
      requested_delivery_date?: string
      delivery_date_tbd?: boolean
      notes?: string
    }) =>
      jsonFetch<IntakeDetail>(`/api/sales-intake/${intakeId}`, {
        method: 'PATCH',
        body: vars,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-inbox'] })
    },
  })

  const lineEdit = useMutation({
    mutationFn: (vars: { lineId: number; edits: Record<string, unknown> }) =>
      jsonFetch(`/api/sales-intake/${intakeId}/lines/${vars.lineId}`, {
        method: 'PATCH',
        body: vars.edits,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-inbox'] })
    },
  })

  const [sendConfirmOpen, setSendConfirmOpen] = useState(false)

  // Header text fields are CONTROLLED and seeded from the server ONLY when
  // the intake id changes (navigating to a different order) — never on a
  // background refetch or a sibling edit. This is the fix for the PO#
  // "wipe": changing the contact mutates `data` (customer_id) but must not
  // touch the PO# / delivery / note the operator typed.
  const [poNumber, setPoNumber] = useState('')
  const [reqDelivery, setReqDelivery] = useState('')
  const [internalNote, setInternalNote] = useState('')
  const [deliveryTbd, setDeliveryTbd] = useState(false)
  const seededForRef = useRef<string | null>(null)
  useEffect(() => {
    if (data && seededForRef.current !== data.id) {
      setPoNumber(data.customer_po_number ?? '')
      setReqDelivery(data.requested_delivery_date ?? '')
      setInternalNote(data.notes ?? '')
      setDeliveryTbd(data.delivery_date_tbd ?? false)
      seededForRef.current = data.id
    }
  }, [data])

  const canConfirmActions = useHasAnyPermission(['admin', 'sales', 'operations', 'purchasing'])

  // Hooks above must run unconditionally; loading / empty render below.
  if (detail.isLoading || !data) {
    return (
      <div data-density="comfortable" className="flex h-full flex-col">
        <header className="flex items-center gap-3 border-b border-border-subtle bg-surface px-6 py-3">
          <Link to="/sales-inbox">
            <Button size="sm" variant="ghost">
              <ArrowLeft size={14} />
              Sales Inbox
            </Button>
          </Link>
          <span className="text-role-section-label">Sales order</span>
        </header>
        <div className="min-h-0 flex-1 overflow-auto p-8 text-tertiary">
          {detail.isLoading ? 'Loading…' : 'No data'}
        </div>
      </div>
    )
  }

  const editable = !isTerminal(data.status)
  const confirmable = !isTerminal(data.status)
  const retryable = data.extraction_state === 'failed'
  const extractionInFlight = data.extraction_state === 'running'
  const linesEditable = editable && !extractionInFlight
  const transitionTargets: Stage[] = allowedTargetsFrom(data.status)

  const hasUnmatchedLine =
    data.lines.length > 0 && data.lines.some((l) => l.matched_product_id == null)
  const isHeaderOnlySend = data.lines.length === 0

  function handleConfirm() {
    confirm.mutate(intakeId, {
      onSuccess: (resp) => {
        const soName = resp?.odoo_so_name ?? resp?.odoo_so_id
        toast.success(soName ? `Sent to Odoo — draft ${soName}` : 'Sent to Odoo')
        navigate({ to: '/sales-inbox' })
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : 'unknown error'
        toast.error(`Send to Odoo failed: ${msg}`, { duration: 10_000 })
      },
    })
  }

  function handleReject() {
    const reason = window.prompt(
      'Dismiss this from the board — why is it not a sales order? (required)',
      '',
    )
    if (!reason || !reason.trim()) return
    reject.mutate(
      { intakeId, reason: reason.trim() },
      { onSuccess: () => navigate({ to: '/sales-inbox' }) },
    )
  }

  function handleRetry() {
    retry.mutate(intakeId)
  }

  return (
    <div data-density="comfortable" className="flex h-full flex-col bg-canvas">
      <header className="flex items-center gap-3 border-b border-border-subtle bg-surface px-6 py-3">
        <Link to="/sales-inbox">
          <Button size="sm" variant="ghost">
            <ArrowLeft size={14} />
            Sales Inbox
          </Button>
        </Link>
        <div className="min-w-0">
          <h1 className="text-role-data-primary truncate">
            {data.customer_name || data.customer_po_number || 'Sales Order'}
            {data.customer_po_number ? (
              <span className="text-role-metadata ml-2 text-tertiary">
                PO {data.customer_po_number}
              </span>
            ) : null}
          </h1>
        </div>
        <div className="ml-2 flex items-center gap-2">
          <Chip size="xs" variant={STAGE_VARIANT[data.status]} dot>
            {STAGE_LABELS[data.status]}
          </Chip>
          <span className="text-xs text-tertiary">
            {data.line_count} line{data.line_count === 1 ? '' : 's'}
          </span>
          {data.uploader ? (
            <span className="text-xs text-tertiary">· uploaded by {data.uploader}</span>
          ) : null}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <a
            href={`/api/sales-intake/${data.id}/attachment`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-xs border border-border-default bg-surface px-2 py-1 text-xs text-secondary hover:bg-surface-hover"
          >
            <FileText size={12} />
            Open file
          </a>
          <Button
            variant={data.acknowledged_at ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => acknowledge.mutate(data.id)}
            disabled={acknowledge.isPending}
            title={
              data.acknowledged_at
                ? `Acknowledged by ${data.acknowledged_by ?? 'unknown'} — click to un-acknowledge`
                : 'Mark as acknowledged'
            }
          >
            <CheckCircle size={14} className="mr-1.5" />
            {data.acknowledged_at ? 'Acknowledged' : 'Acknowledge'}
          </Button>
          {retryable ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleRetry}
              disabled={retry.isPending}
            >
              <RefreshCw size={14} className="mr-1.5" />
              Re-extract
            </Button>
          ) : null}
          {confirmable ? (
            <>
              {data.odoo_so_id ? (
                <a
                  href={data.odoo_so_url ?? odooUrl('sale.order', data.odoo_so_id)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium bg-surface border border-border-default text-primary hover:bg-surface-sunk transition-colors"
                >
                  <ExternalLink size={14} />
                  View in Odoo ({data.odoo_so_name ?? `#${data.odoo_so_id}`})
                </a>
              ) : (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setSendConfirmOpen(true)}
                  disabled={confirm.isPending || data.customer_id == null || hasUnmatchedLine || duplicate?.has_duplicates === true}
                  title={
                    duplicate?.has_duplicates
                      ? 'Duplicate SO detected — link to the existing order first'
                      : data.customer_id == null
                        ? 'Set the customer before sending'
                        : hasUnmatchedLine
                          ? 'One or more lines have no SPI product — match or remove them first'
                          : isHeaderOnlySend
                            ? 'No lines were extracted — Odoo SO will be header-only; add lines in Odoo'
                            : undefined
                  }
                >
                  <Send size={14} className="mr-1.5" />
                  Send to Odoo
                </Button>
              )}
              {/* Destructive action isolated at the far-right end, separated
                  by a divider so it never sits between the non-destructive
                  actions and the primary Send to Odoo CTA. */}
              <span className="mx-1 h-5 w-px bg-border-default" aria-hidden="true" />
              <Button
                variant="danger"
                size="sm"
                onClick={handleReject}
                disabled={reject.isPending}
                title="Not a sales order (e.g. a vendor confirmation CARLA mis-staged) — remove it from the board"
              >
                <X size={14} className="mr-1.5" />
                Remove
              </Button>
            </>
          ) : null}
        </div>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_minmax(380px,460px)]">
        {/* LEFT — rich working content */}
        <div className="min-h-0 overflow-y-auto px-6 py-4">
          <div className="flex flex-col gap-4">
            {duplicate?.has_duplicates ? (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-sm border border-warning bg-warning-soft px-3 py-2 text-sm"
              >
                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-warning" />
                <div className="flex-1">
                  <div className="font-medium text-primary">
                    Duplicate detected — {duplicate.matches.length} existing Odoo SO match this customer + PO #
                  </div>
                  <ul className="mt-2 space-y-1.5 text-xs text-secondary">
                    {duplicate.matches.slice(0, 3).map((m) => (
                      <li key={m.id} className="flex items-center gap-3">
                        <span>
                          <a
                            href={odooUrl('sale.order', m.id)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-link hover:underline"
                          >
                            {m.name}
                          </a>
                          {' · '}{m.state}
                          {m.amount_total != null ? ` · $${m.amount_total.toFixed(2)}` : ''}
                          {m.date_order ? ` · ${m.date_order.slice(0, 10)}` : ''}
                        </span>
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={linkOdooSo.isPending}
                          onClick={() => {
                            linkOdooSo.mutate(
                              { intakeId: data.id, odoo_so_id: m.id, odoo_so_name: m.name },
                              {
                                onSuccess: () => {
                                  setDuplicate(null)
                                  toast.success(`Linked to ${m.name}`)
                                },
                                onError: (err) => {
                                  const msg = err instanceof Error ? err.message : 'unknown error'
                                  toast.error(`Link failed: ${msg}`)
                                },
                              },
                            )
                          }}
                        >
                          Link to {m.name}
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : null}

            {/* Pricelist warning — surfaces not_on_pricelist anomalies flagged
                during CARLA extraction so the operator knows to add products
                to the customer's pricelist before confirming. Operator can
                dismiss after taking action; anomalies recorded after the
                dismissal timestamp will reappear automatically. */}
            {(() => {
              const dismissedAt = data.warnings_dismissed_at ?? null
              const offPricelist = data.stage_history.filter(
                (e) => e.kind === 'anomaly' && e.anomaly_type === 'not_on_pricelist'
                  && (dismissedAt == null || e.at > dismissedAt),
              )
              if (offPricelist.length === 0) return null
              return (
                <div
                  role="alert"
                  className="relative flex items-start gap-2 rounded-sm border border-warning bg-warning-soft px-3 py-2 pr-8 text-sm"
                >
                  <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-warning" />
                  <div className="flex-1">
                    <div className="font-medium text-primary">
                      {offPricelist.length === 1
                        ? '1 product is not on this customer\'s pricelist'
                        : `${offPricelist.length} products are not on this customer's pricelist`}
                    </div>
                    <ul className="mt-1 text-xs text-secondary list-disc pl-4">
                      {offPricelist.map((e, i) => (
                        <li key={i}>{e.details}</li>
                      ))}
                    </ul>
                    {data.customer_id ? (
                      <Link
                        to="/pricelists"
                        search={{ partner: data.customer_id }}
                        className="mt-1 inline-block text-xs text-info hover:underline"
                      >
                        Open Customer Pricelists →
                      </Link>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    aria-label="Dismiss warning"
                    disabled={dismissWarnings.isPending}
                    onClick={() => dismissWarnings.mutate(intakeId)}
                    className="absolute right-2 top-2 rounded p-0.5 text-tertiary hover:text-primary disabled:opacity-40"
                  >
                    <X size={14} />
                  </button>
                </div>
              )
            })()}

            {/* Header form */}
            <section className="rounded-sm border border-border-subtle bg-surface p-3">
              <h3 className="text-role-section-label m-0 mb-2">Header</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-tertiary mb-1">Contact</label>
                  <PartnerPicker
                    value={
                      data.customer_id != null && data.customer_name
                        ? [data.customer_id, data.customer_name]
                        : null
                    }
                    onChange={(picked) => {
                      if (picked) {
                        // The contact is the PERSON who placed the order,
                        // never the company. Changing it must not disturb
                        // the PO# / delivery / note (those are local state).
                        headerEdit.mutate({
                          customer_id: picked[0],
                          customer_name: picked[1],
                        })
                      }
                    }}
                    kind="customer_contact"
                    placeholder="Search contact (person)…"
                    disabled={!editable}
                  />
                </div>
                <div>
                  <label className="block text-xs text-tertiary mb-1">Customer PO #</label>
                  <input
                    type="text"
                    value={poNumber}
                    onChange={(e) => setPoNumber(e.target.value)}
                    disabled={!editable}
                    onBlur={() => {
                      const v = poNumber.trim()
                      if (v !== (data.customer_po_number ?? '')) {
                        headerEdit.mutate({ customer_po_number: v })
                      }
                    }}
                    className="w-full rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary focus:border-border-focus focus:outline-none disabled:bg-surface-sunk disabled:text-text-disabled"
                  />
                </div>
                <div>
                  <label className="block text-xs text-tertiary mb-1">Requested delivery</label>
                  <input
                    type="date"
                    value={deliveryTbd ? '' : reqDelivery}
                    onChange={(e) => setReqDelivery(e.target.value)}
                    disabled={!editable || deliveryTbd}
                    onBlur={() => {
                      if (!deliveryTbd && reqDelivery !== (data.requested_delivery_date ?? '')) {
                        headerEdit.mutate({ requested_delivery_date: reqDelivery })
                      }
                    }}
                    className="w-full rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary focus:border-border-focus focus:outline-none disabled:bg-surface-sunk disabled:text-text-disabled"
                  />
                  <label className="mt-1 flex items-center gap-1.5 text-xs text-tertiary">
                    <input
                      type="checkbox"
                      checked={deliveryTbd}
                      disabled={!editable}
                      onChange={(e) => {
                        const next = e.target.checked
                        setDeliveryTbd(next)
                        if (next) setReqDelivery('')
                        // Server clears requested_delivery_date when TBD set.
                        headerEdit.mutate({ delivery_date_tbd: next })
                      }}
                    />
                    Delivery date TBD
                  </label>
                  {deliveryTbd ? (
                    <span className="mt-1 block text-xs text-warning">
                      TBD — order can be confirmed; won&apos;t show Late in the warehouse.
                    </span>
                  ) : null}
                </div>
                <div>
                  <label className="block text-xs text-tertiary mb-1">Internal note</label>
                  <input
                    type="text"
                    value={internalNote}
                    onChange={(e) => setInternalNote(e.target.value)}
                    disabled={!editable}
                    onBlur={() => {
                      const v = internalNote.trim()
                      if (v !== (data.notes ?? '')) {
                        headerEdit.mutate({ notes: v })
                      }
                    }}
                    className="w-full rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary focus:border-border-focus focus:outline-none disabled:bg-surface-sunk disabled:text-text-disabled"
                  />
                </div>
              </div>
            </section>

            {/* Draft preview — exactly what gets sent to Odoo */}
            <DraftSummaryCard intake={data} />

            {/* Lines */}
            <section>
              <h3 className="text-role-section-label m-0 mb-2">Draft Order Lines</h3>
              {extractionInFlight ? (
                <div className="mb-2 flex items-start gap-2 rounded-sm border border-warning bg-warning-soft px-3 py-2 text-sm">
                  <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-warning" />
                  <span>
                    Extraction is still running. Line edits are disabled because the
                    parser will overwrite the grid when it finishes. Header edits
                    remain available.
                  </span>
                </div>
              ) : null}
              <SalesIntakeLineGrid
                lines={data.lines}
                disabled={!linesEditable}
                onLineEdit={(lineId, edits) => {
                  lineEdit.mutate({ lineId, edits })
                }}
              />
            </section>

            {/* Pipeline transition controls — above the reply composer so
                operators move the order's stage before drafting a reply. */}
            {transitionTargets.length > 0 ? (
              <section className="rounded-sm border border-border-subtle bg-surface p-3">
                <h3 className="text-role-section-label m-0 mb-2">Move pipeline</h3>
                <PipelineTransitionPicker
                  targets={transitionTargets}
                  isPending={transition.isPending}
                  onChoose={(target, note) => {
                    transition.mutate(
                      { intakeId, targetStage: target, note },
                      {
                        onError: (err) => {
                          const msg = err instanceof Error ? err.message : 'transition failed'
                          toast.error(msg, { duration: 5000 })
                        },
                      },
                    )
                  }}
                />
              </section>
            ) : null}

            {/* Reply to the customer — the inbox is the source of truth for
                the order's conversation, not just its data. Mirrors
                /regulatory + /support: composer + signature + threaded send. */}
            {editable ? <SendResponseSection intakeId={data.id} /> : null}

            {/* Stage history timeline. */}
            {data.stage_history.length > 0 ? (
              <section className="rounded-sm border border-border-subtle bg-surface p-3">
                <h3 className="text-role-section-label m-0 mb-2">History</h3>
                <StageHistoryTimeline entries={data.stage_history} />
              </section>
            ) : null}

            {data.status === 'sales_order_cancelled' && data.rejected_reason ? (
              <section className="rounded-sm border border-danger bg-danger-soft p-3 text-sm">
                <strong>Cancelled:</strong> {data.rejected_reason}
              </section>
            ) : null}

            {data.odoo_so_name ? (
              <p className="m-0 text-xs text-tertiary">
                Created Odoo SO{' '}
                {data.odoo_so_url ? (
                  <a
                    href={data.odoo_so_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-info hover:underline"
                  >
                    <strong>{data.odoo_so_name}</strong>
                  </a>
                ) : (
                  <strong className="text-secondary">{data.odoo_so_name}</strong>
                )}
              </p>
            ) : null}
          </div>
        </div>

        {/* RIGHT — source email preview + CARLA chat */}
        <div className="flex min-h-0 flex-col border-l border-border-subtle">
          <div className="min-h-0 flex-1 overflow-y-auto border-b border-border-subtle">
            <div className="border-b border-border-subtle bg-surface-sunk px-3 py-2">
              <span className="text-role-section-label text-secondary">Source email</span>
            </div>
            <EmailThreadPanel
              endpoint={`/api/sales-intake/${intakeId}/email`}
              latestMessageIndexId={data.source_email_id}
            />
          </div>
          <div className="min-h-0 flex-1 overflow-hidden">
            <SalesIntakeChatPanel intake={data} canConfirmActions={canConfirmActions} />
          </div>
        </div>
      </div>

      <Modal
        open={sendConfirmOpen}
        onOpenChange={setSendConfirmOpen}
        tone={isHeaderOnlySend ? 'warning' : 'info'}
        title={isHeaderOnlySend ? 'Send header-only draft?' : 'Send this to Odoo?'}
        description={
          isHeaderOnlySend ? (
            <>
              CARLA extracted <strong>0 lines</strong> from this PDF. The draft{' '}
              <strong>sale.order</strong> in Odoo will carry the header — customer,
              ship-to, PO #, delivery date — but no line items. Add lines manually in
              Odoo, then have sales confirm the order.
            </>
          ) : (
            <>
              This creates a draft <strong>sale.order</strong> in Odoo with exactly the
              lines previewed. Sales does the final confirm in Odoo to actually book the
              order. You can edit the draft in Odoo after sending.
            </>
          )
        }
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setSendConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setSendConfirmOpen(false)
                handleConfirm()
              }}
              disabled={confirm.isPending}
            >
              <Send size={14} className="mr-1.5" />
              {isHeaderOnlySend ? 'Send header anyway' : 'Send to Odoo'}
            </Button>
          </>
        }
      />
    </div>
  )
}

/**
 * Compose + send a customer reply email — mirrors the regulatory/support
 * work-view composer. Reuses the shared RichTextEditor + SignaturePicker +
 * applySignatureToBody; sends through MailDesk via /send-response so the
 * reply threads into the source conversation and appears back in the
 * EmailThreadPanel. Recipient is operator-entered (the inbound sender is
 * visible in the source-email panel on the right).
 */
function SendResponseSection({ intakeId }: { intakeId: string }) {
  const [email, setEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const send = useSendIntakeResponse()
  const canSend = !!email.trim() && !!subject.trim() && !!body.trim()

  return (
    <section className="rounded-sm border border-border-subtle bg-surface p-3">
      <h3 className="text-role-section-label m-0 mb-2">Reply to customer</h3>
      <div className="flex flex-col gap-2">
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="email"
            placeholder="recipient@customer.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            placeholder="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>
        <RichTextEditor value={body} onChange={setBody} placeholder="Write your reply…" />
        <div className="flex items-center gap-2">
          <span className="text-role-metadata text-tertiary">Signature</span>
          <SignaturePicker
            endpoint={`/api/sales-intake/${intakeId}/signatures`}
            onApply={(sig) => setBody((b) => applySignatureToBody(b, sig))}
            applyDefaultOnLoad
          />
        </div>
        {body.trim() ? (
          <div className="rounded-sm border border-border-subtle bg-surface-sunk p-2">
            <div className="text-role-metadata text-tertiary mb-1">Preview</div>
            <div
              className="text-sm text-secondary [&_a]:text-accent [&_a]:underline"
              // Sanitized — never render raw email HTML to operators.
              dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(body) }}
            />
          </div>
        ) : null}
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            disabled={!canSend || send.isPending}
            onClick={() => {
              if (!window.confirm(`Send this reply to ${email.trim()}?`)) return
              send.mutate(
                { id: intakeId, target_email: email.trim(), subject: subject.trim(), body },
                {
                  onSuccess: () => {
                    toast.success(`Reply sent to ${email.trim()}`)
                    setSubject('')
                    setBody('')
                  },
                  onError: (err) => {
                    const msg = err instanceof Error ? err.message : 'send failed'
                    toast.error(`Send failed: ${msg}`, { duration: 8000 })
                  },
                },
              )
            }}
          >
            <Send size={14} className="mr-1.5" />
            Send reply
          </Button>
        </div>
      </div>
    </section>
  )
}

/**
 * Small picker + note input + apply button for operator-driven commercial
 * pipeline transitions. Rehomed verbatim from SalesIntakeDetailDrawer.
 */
function PipelineTransitionPicker({
  targets,
  isPending,
  onChoose,
}: {
  targets: Stage[]
  isPending: boolean
  onChoose: (target: Stage, note?: string) => void
}) {
  const [target, setTarget] = useState<Stage | ''>('')
  const [note, setNote] = useState('')

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={target}
          onChange={(e) => setTarget(e.target.value as Stage | '')}
          aria-label="Target stage"
          className="min-w-[14rem]"
        >
          <option value="">Choose target stage…</option>
          {targets.map((t) => (
            <option key={t} value={t}>
              {STAGE_LABELS[t]}
            </option>
          ))}
        </Select>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note (why)"
          className="min-w-[16rem] flex-1 rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary focus:border-border-focus focus:outline-none"
        />
        <Button
          variant="secondary"
          size="sm"
          disabled={!target || isPending}
          onClick={() => {
            if (!target) return
            onChoose(target, note.trim() || undefined)
            setTarget('')
            setNote('')
          }}
        >
          <ArrowRight size={14} className="mr-1.5" />
          Move
        </Button>
      </div>
      <p className="m-0 text-xs text-tertiary">
        Available targets are operator-driven moves. <em>Sales Order Confirmed and
        Sent</em> is set automatically when Odoo confirms the SO.
      </p>
    </div>
  )
}
